import { updatePaginationState } from "@/cache/appstate/WriteQueries";
import { FramerButton, RadioButton, TagItem, TwGlobalFilter } from "@/components/shared";
import { AssetFinancingStatus, GetPaginatedInvestmentsQueryVariables } from "@/graphql/__generated__/graphql-operations";
import { usePaginationState } from "@/hooks/cache/appState/usePaginationState";
import { PaginationState } from "@/types";
import { ArrowPathIcon, CheckIcon, FunnelIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { ChangeEvent, JSX } from "react";
import { toast } from "react-toastify";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useTheme } from "@/context/ThemeProvider";
import { Card } from "@/components/ui/card";

export type propsPoolsFilterComponent = {
  onFilterSubmit: (_variables: GetPaginatedInvestmentsQueryVariables) => void;
  displayAmount: number;
}

type RadioOption = {
  label: string;
  value: AssetFinancingStatus;
}

// Constants
const afpStatusOptions: RadioOption[] = [
  { label: "Open", value: AssetFinancingStatus.Open },
  { label: "Closed", value: AssetFinancingStatus.Closed },
  { label: "Active", value: AssetFinancingStatus.Active },
];

/**
 * PoolsFilter component for filtering investment pools.
 *
 * @param {propsPoolsFilterComponent} props - The properties for the component.
 * @param {(_variables: GetPaginatedInvestmentsQueryVariables) => void} props.onFilterSubmit - The function to call when the filter is submitted.
 * @param {number} props.displayAmount - The number of items to display.
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * ```tsx
 *
 * return (
 *   <PoolsFilter onFilterSubmit={handleFilterSubmit} displayAmount={10} />
 * )
 * ```
 */
const PoolsFilter = (props: propsPoolsFilterComponent): JSX.Element => {

  // Props
  const { onFilterSubmit, displayAmount } = props;


  // Hooks
  const { t } = useTranslation();
  const paginationState: PaginationState["paginatedInvestments"] = usePaginationState("paginatedInvestments");
  const { filters: { companyName, status } } = paginationState;
  const { darkMode } = useTheme();

  // Constants
  const backgroundColour = darkMode ? "bg-mfdarklighter text-white border-transparent shadow-gray-900": "bg-white";

  /**
   * OnChange Handler for Filter input field.
   * @param {ChangeEvent<HTMLInputElement>} event - The change event.
   */
  function handleFilterOnChange(event: ChangeEvent<HTMLInputElement>) {
    updatePaginationState({ paginatedInvestments: { filters: { companyName: event.target.value } } });
  }

  /**
   * OnChange Handler for Radio buttons.
   * @param {string} key - The key of the filter.
   * @param {string} value - The value of the filter.
   */
  const onChangeRadioButton = (key: string, value: string) => {
    if (key === "status") {
      updatePaginationState({ paginatedInvestments: { filters: { status: value as AssetFinancingStatus }}});
    }
  };

  /**
   * OnClick Handler for Reset button.
   */
  function handleResetClick() {
    // Call the filter submit function
    onFilterSubmit({ first: displayAmount as number, companyName: "", status: null });
    toast.success(t("common:text.filterremoved"), {toastId: "investmentFilterRemoved"});
  }

  /**
   * OnClick Handler for Submit button.
   */
  function handleSubmitClick() {
    // Call the filter submit function
    onFilterSubmit({ first: displayAmount, companyName, status });
    toast.success(t("common:text.filterapplied"), {toastId: "investmentFilterApplied"});
  }

  return (
    <Card className="mb-4 bg-white px-4 py-3 shadow-lg dark:bg-mfdarklight">
      <div className="flex items-center w-full space-x-2">
        <TwGlobalFilter
          value={companyName ?? ""}
          placeholder={t("portfoliopage.filter.title")}
          onChange={handleFilterOnChange}
          className="flex-grow"
        />

        {/* Filter box */}
        <Popover>
          <PopoverTrigger
            data-cy="pools-filter-popover-trigger"
            className="flex item-center h-[36px] dark:border-white relative select-none"
          >
            <Card
              className="flex flex-row item-center bg-transparent h-[36px] md:w-[200px] px-2 py-1 relative select-none shadow-none"
            >
              <div className="sm:mr-2 h-full items-center flex shrink-0">
                <FunnelIcon className="w-5 h-5 text-gray-400 dark:text-white" />
                {
                  status === null &&
                    <p className="hidden sm:block text-sm text-gray-400 pl-2">
                      {t("portfoliopage.filter.selectstatus")}
                    </p>
                }
              </div>
              <div className="hidden sm:flex items-center space-x-2 select-none mt-[2px]">
                {
                  status !== null &&
                    <TagItem label={status as string} />
                }
              </div>
            </Card>
          </PopoverTrigger>
          {/* Content */}
          <PopoverContent
            data-cy="pools-filter-popover-content"
            align="start"
            className={`shadow-lg h-auto p-4 min-w-[130px] md:min-w-[200px] max-w-min rounded-md dark:text-white mt-1 ${backgroundColour}`}
          >
            {/* Status filters */}
            {afpStatusOptions.map(option => (
              <RadioButton
                key={option.value}
                option={option}
                checked={status === option.value}
                onChange={(value) => onChangeRadioButton("status", value as string)}
              />
            ))}
          </PopoverContent>
        </Popover>

        {/* Action buttons */}
        <div className="flex justify-end items-center gap-x-2">
          {/* Reset button */}
          <FramerButton
            onClick={handleResetClick}
            variant="outline-primary"
            className="min-w-[50px] lg:min-w-[100px]"
          >
            <ArrowPathIcon data-cy="pools-filter-reset-icon" className="w-5 h-5 lg:hidden" />
            <div className="hidden lg:block">
              {t("common:buttons.reset")}
            </div>
          </FramerButton>

          {/* Submit button */}
          <FramerButton
            onClick={handleSubmitClick}
            className="bg-mforange text-white min-w-[50px] lg:min-w-[100px] hover:bg-mforange/80"
          >
            <CheckIcon data-cy="pools-filter-submit-icon" className="w-5 h-5 lg:hidden" />
            <div className="hidden lg:block">
              {t("common:buttons.submit")}
            </div>
          </FramerButton>
        </div>
      </div>
    </Card>
  );
};

export default PoolsFilter;
